import {
  Box,
  BoxProps,
  Center,
  Image,
  ImageProps,
  Wrap,
  WrapItem,
  WrapProps,
  chakra,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import {
  EImageGroupImageAlignment,
  ELinkSource,
} from '@/graphql-types/globalTypes';
import { BrokenImageFallback } from '@/modules/website-builder/components/BrokenImageFallback';
import { BACKGROUND_COLOR } from '@/modules/website-builder/attributes';
import { ImageGroup, ImageGroupImage } from '@/modules/website-builder/types';
import { getImageAltText } from '@/modules/website-builder/utilities';
import { getExternalUrl } from '@/utilities/getExternalUrl';

const JUSTIFY: Record<EImageGroupImageAlignment, WrapProps['justify']> = {
  [EImageGroupImageAlignment.LEFT]: 'left',
  [EImageGroupImageAlignment.CENTER]: 'center',
};

const IMAGE_FALLBACK_ASPECT_RATIO = '4/3';

const ImageGroupBrokenImage: React.FC<{
  altText: string;
  height: string;
}> = ({ altText, height }) => (
  <BrokenImageFallback
    altText={altText}
    height={height}
    maxH={height}
    aspectRatio={IMAGE_FALLBACK_ASPECT_RATIO}
  />
);

const ImageLink: React.FC<
  ImageProps & {
    image: ImageGroupImage;
    height: string;
  }
> = ({ image, height, ...props }) => {
  const altText = getImageAltText(image);
  const href =
    image.linkSource === ELinkSource.CREATED_PAGE
      ? image.linkPage?.href
      : image.linkSource === ELinkSource.LINK
        ? image.linkHref
        : undefined;

  const imageElement = (
    <Image
      {...props}
      alt={altText}
      h={height}
      maxH={height}
      objectFit="contain"
      cursor={href ? 'pointer' : props.cursor}
      fallback={<ImageGroupBrokenImage altText={altText} height={height} />}
    />
  );

  if (href) {
    if (image.linkIsExternal) {
      return (
        <chakra.a target="_blank" rel="noreferrer" href={getExternalUrl(href)}>
          {imageElement}
        </chakra.a>
      );
    }

    return <NextLink href={href}>{imageElement}</NextLink>;
  }

  return imageElement;
};

export const ImageGroupRender: React.FC<
  {
    imageGroup: ImageGroup;
  } & BoxProps
> = ({ imageGroup, ...props }) => {
  const height = imageGroup.options.imageHeight + 'px';

  return (
    <Box
      w="full"
      backgroundColor={BACKGROUND_COLOR[imageGroup.options.backgroundColor]}
      {...props}
    >
      <Wrap
        w="full"
        maxW="container.xl"
        px="4"
        mx="auto"
        justify={JUSTIFY[imageGroup.options.imageAlignment]}
        spacing="5"
      >
        {imageGroup.images.map((image, index) => (
          <WrapItem key={index}>
            <Center h="full">
              <ImageLink
                image={image}
                height={height}
                src={image.imageProxy ?? undefined}
                maxH={height}
              />
            </Center>
          </WrapItem>
        ))}
      </Wrap>
    </Box>
  );
};
